import { supabase } from './supabase.js';
import { getPublicSiteBase } from '../constants/siteConfig.js';
import { extractInvokeErrorMessage } from './groovesStripeService.js';
import { assertPaymentsEnabled, LAUNCH_PHASE_PAYMENTS_MSG } from './launchPhaseClient.js';

export function marketplaceCheckoutReturnBaseUrl() {
  return getPublicSiteBase();
}

export function mapMarketplaceCheckoutError(error) {
  const code = String(error ?? '').trim();
  const lower = code.toLowerCase();
  if (!code) return 'No se pudo iniciar el pago.';
  if (lower.includes('physical_delivery') || lower.includes('entrega física')) {
    return 'Para entrega física, acuerda la compra con el vendedor por chat.';
  }
  if (lower.includes('listing_already_in_sale') || lower.includes('compra en curso')) {
    return 'Ya hay una compra en curso para este anuncio.';
  }
  if (lower.includes('listing_not_available') || lower.includes('vendido')) {
    return 'Este anuncio ya no está disponible.';
  }
  if (lower.includes('cannot_buy_own')) {
    return 'No puedes comprar tu propio anuncio.';
  }
  if (lower.includes('launch_phase_blocked') || lower.includes('fase del lanzamiento')) {
    return LAUNCH_PHASE_PAYMENTS_MSG;
  }
  if (lower.includes('verifier_partner') || lower.includes('tienda verificadora')) {
    return 'Elige una tienda verificadora de la lista para continuar.';
  }
  if (lower.includes('pickup_partner')) {
    return 'Elige un punto de recogida de la lista para continuar.';
  }
  if (lower.includes('shipping_address_required') || lower.includes('dirección')) {
    return 'Completa la dirección de envío para continuar.';
  }
  if (lower.includes('buyer_delivery_mode') || lower.includes('modalidad')) {
    return 'Elige cómo quieres recibir el disco desde el partner.';
  }
  if (lower.includes('buyer_delivery_mode_not_supported')) {
    return 'Este partner no ofrece esa forma de entrega. Elige otra modalidad o otro local.';
  }
  if (lower.includes('shipping_not_offered')) {
    return 'Este anuncio no ofrece esa opción de entrega.';
  }
  if (lower.includes('listing_is_auction')) {
    return 'Este artículo es una subasta.';
  }
  if (lower.includes('unauthorized') || lower.includes('sesión')) {
    return 'Inicia sesión para comprar.';
  }
  if (lower.includes('payment_not_paid') || lower.includes('aún no marca')) {
    return 'El pago se está procesando. Espera unos segundos.';
  }
  if (lower.includes('stripe') || lower.includes('pago')) {
    return 'No se pudo abrir la pasarela de pago. Inténtalo más tarde.';
  }
  if (lower.includes('failed to fetch') || lower.includes('network')) {
    return 'Error de conexión. Revisa tu red e inténtalo de nuevo.';
  }
  return code;
}

export async function createListingCheckoutSession({
  listingId,
  shippingOption,
  verifierPartnerId,
  pickupPartnerId,
  buyerPartnerDeliveryMode,
  buyerShippingAddress,
}) {
  const lid = String(listingId ?? '').trim();
  if (!lid) return { url: null, error: 'missing_listing' };

  const phaseBlock = await assertPaymentsEnabled();
  if (phaseBlock) return { url: null, error: phaseBlock };

  const body = {
    listing_id: lid,
    shipping_option: shippingOption,
    return_base_url: marketplaceCheckoutReturnBaseUrl(),
  };
  const vpid = String(verifierPartnerId ?? '').trim();
  if (shippingOption === 2 && vpid) {
    body.verifier_partner_id = vpid;
  }
  const ppid = String(pickupPartnerId ?? '').trim();
  if (shippingOption === 1 && ppid) {
    body.pickup_partner_id = ppid;
  }
  if (shippingOption === 1 || shippingOption === 2) {
    const mode = String(buyerPartnerDeliveryMode ?? '').trim();
    if (mode) body.buyer_partner_delivery_mode = mode;
    if (mode === 'ship_to_buyer' && buyerShippingAddress) {
      body.buyer_shipping_address = buyerShippingAddress;
    }
  }

  const { data, error } = await supabase.functions.invoke('create-checkout-listing', {
    body,
  });

  if (data && typeof data === 'object') {
    if (data.error) {
      return { url: null, error: mapMarketplaceCheckoutError(data.message || data.error) };
    }
    if (data.ok && data.url) {
      return {
        url: data.url,
        error: null,
        sessionId: data.session_id ?? null,
        totalEur: data.total_eur ?? null,
      };
    }
  }

  if (error) {
    const raw = await extractInvokeErrorMessage(error);
    return { url: null, error: mapMarketplaceCheckoutError(raw) };
  }

  return { url: null, error: mapMarketplaceCheckoutError(data?.message || 'no_url') };
}

export async function startListingCheckout({
  listingId,
  shippingOption,
  verifierPartnerId,
  pickupPartnerId,
  buyerPartnerDeliveryMode,
  buyerShippingAddress,
}) {
  const { url, error } = await createListingCheckoutSession({
    listingId,
    shippingOption,
    verifierPartnerId,
    pickupPartnerId,
    buyerPartnerDeliveryMode,
    buyerShippingAddress,
  });
  if (!url) {
    return { ok: false, error: error || 'No se pudo iniciar el pago.' };
  }
  window.location.assign(url);
  return { ok: true };
}

export async function fulfillMarketplaceCheckoutSession(stripeSessionId) {
  const sid = String(stripeSessionId ?? '').trim();
  if (!sid) {
    return { ok: false, fulfilled: false, transactionId: null, error: 'missing_session' };
  }

  const { data, error } = await supabase.functions.invoke('fulfill-marketplace-checkout', {
    body: { session_id: sid },
  });

  if (data && typeof data === 'object') {
    if (data.ok) {
      return {
        ok: true,
        fulfilled: !!data.fulfilled,
        transactionId: data.transaction_id ?? null,
        error: null,
      };
    }
    if (data.error) {
      return {
        ok: false,
        fulfilled: false,
        transactionId: null,
        error: mapMarketplaceCheckoutError(data.message || data.error),
      };
    }
  }

  if (error) {
    const raw = await extractInvokeErrorMessage(error);
    return {
      ok: false,
      fulfilled: false,
      transactionId: null,
      error: mapMarketplaceCheckoutError(raw),
    };
  }

  return {
    ok: false,
    fulfilled: false,
    transactionId: null,
    error: mapMarketplaceCheckoutError('fulfill_failed'),
  };
}

export async function releaseMyMarketplaceCheckoutReservation(stripeSessionId) {
  const sid = String(stripeSessionId ?? '').trim();
  if (!sid) return { error: null };
  const { error } = await supabase.rpc('release_my_marketplace_checkout_reservation', {
    p_stripe_session_id: sid,
  });
  if (error) return { error: error.message };
  return { error: null };
}

export async function fetchMarketplaceCheckoutSessionStatus(stripeSessionId) {
  const sid = String(stripeSessionId ?? '').trim();
  if (!sid) return { data: null, error: 'missing_session' };
  const { data, error } = await supabase
    .from('marketplace_stripe_checkout_sessions')
    .select(
      'listing_id, shipping_option, amount_cents, listing_amount, shipping_cost, transaction_id, fulfilled_at, created_at'
    )
    .eq('stripe_session_id', sid)
    .maybeSingle();
  if (error) return { data: null, error: error.message };
  return { data, error: null };
}
